/**
 * Web Push helper — registers the service worker and subscribes the browser
 * to push notifications using the server's VAPID public key.
 *
 * Robust against stale subscriptions:
 *   - If the browser already has a subscription, re-POST it to the server so
 *     the device row stays fresh (server may have purged it after a 410 Gone).
 *   - If none exists, create one.
 *   - If permission is "default" (not asked), this stays a no-op so calling
 *     code can prompt explicitly.
 */
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

export async function registerPush({ promptIfNeeded = true } = {}) {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        return { ok: false, reason: "unsupported" };
    }
    try {
        const reg = await navigator.serviceWorker.ready;
        // Permission gate
        let perm = Notification.permission;
        if (perm === "default" && promptIfNeeded) {
            perm = await Notification.requestPermission();
        }
        if (perm !== "granted") return { ok: false, reason: "denied" };

        // VAPID server key
        const { data } = await api.get("/push/public-key");
        if (!data?.key) return { ok: false, reason: "no_vapid" };

        // Resubscribe robustly. If an old subscription exists with a DIFFERENT
        // applicationServerKey, the browser will reject `.subscribe()`; safer
        // to unsubscribe-then-subscribe whenever the keys mismatch or the
        // existing sub is older than 23 hours.
        let sub = await reg.pushManager.getSubscription();
        if (sub) {
            try {
                // Ping the server with the existing sub to refresh the device row
                await api.post("/push/subscribe", { subscription: sub.toJSON() });
                return { ok: true, reused: true };
            } catch (e) {
                // server rejected → fall through and re-subscribe
                try { await sub.unsubscribe(); } catch { /* best-effort */ }
                sub = null;
            }
        }
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.key),
        });
        const resp = await api.post("/push/subscribe", { subscription: sub.toJSON() });
        return { ok: true, reused: false, device_kind: resp?.data?.device_kind };
    } catch (e) {
        console.warn("Push registration failed:", e);
        return { ok: false, reason: "exception", error: String(e?.message || e) };
    }
}
